import React, { useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 9 },
  clickableIcons: false,
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

/**
 * Small, lightweight map for the row-expand panel.
 * Shows pickup (green), delivery (red), optional current vehicle position (blue),
 * and a straight line between pickup and delivery as a visual cue.
 */
function OrderMiniMap({ order, vehiclePosition }) {
  const mapRef = useRef(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  const pickupLat = Number(order?.pickupLat || order?.pickup?.lat);
  const pickupLng = Number(order?.pickupLng || order?.pickup?.lng);
  const deliveryLat = Number(order?.deliveryLat || order?.delivery?.lat);
  const deliveryLng = Number(order?.deliveryLng || order?.delivery?.lng);

  const hasPickup = !!(pickupLat && pickupLng);
  const hasDelivery = !!(deliveryLat && deliveryLng);

  const onLoadMap = useCallback((map) => { mapRef.current = map; }, []);

  // Fit bounds whenever points change
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (hasPickup) bounds.extend({ lat: pickupLat, lng: pickupLng });
    if (hasDelivery) bounds.extend({ lat: deliveryLat, lng: deliveryLng });
    if (vehiclePosition) bounds.extend({ lat: vehiclePosition.latitude, lng: vehiclePosition.longitude });
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, { top: 30, bottom: 30, left: 30, right: 30 });
  }, [isLoaded, hasPickup, hasDelivery, pickupLat, pickupLng, deliveryLat, deliveryLng, vehiclePosition]);

  if (!isLoaded) {
    return (
      <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: 10 }}>
        <CircularProgress size={20} style={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (!hasPickup && !hasDelivery) {
    return (
      <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: 10, border: '1px dashed #cbd5e1' }}>
        <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Sem coordenadas</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ height: '100%', width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={hasPickup ? { lat: pickupLat, lng: pickupLng } : { lat: 39.5, lng: -8.0 }}
        zoom={12}
        options={MAP_OPTIONS}
        onLoad={onLoadMap}
      >
        {hasPickup && hasDelivery && (
          <Polyline
            path={[
              { lat: pickupLat, lng: pickupLng },
              { lat: deliveryLat, lng: deliveryLng },
            ]}
            options={{
              strokeColor: '#64748b',
              strokeOpacity: 0.5,
              strokeWeight: 2,
              geodesic: true,
              clickable: false,
              icons: [{
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                offset: '0',
                repeat: '14px',
              }],
            }}
          />
        )}
        {hasPickup && (
          <Marker
            position={{ lat: pickupLat, lng: pickupLng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#22c55e',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2.5,
            }}
            title={order?.pickupName || 'Recolha'}
          />
        )}
        {hasDelivery && (
          <Marker
            position={{ lat: deliveryLat, lng: deliveryLng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#ef4444',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2.5,
            }}
            title={order?.deliveryName || 'Entrega'}
          />
        )}
        {vehiclePosition && (
          <Marker
            position={{ lat: vehiclePosition.latitude, lng: vehiclePosition.longitude }}
            icon={{
              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 5,
              fillColor: '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              rotation: vehiclePosition.course || 0,
            }}
            title="Posição actual do veículo"
          />
        )}
      </GoogleMap>
    </Box>
  );
}

export default React.memo(OrderMiniMap);
